import { IonIcon } from "@ionic/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type FieldApi } from "@tanstack/react-form";
import { useQuery } from "@tanstack/react-query";
import { closeOutline, informationCircleOutline } from "ionicons/icons";
import { toast } from "sonner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { queryOptions_custom_field_file_url } from "@/lib/query/file/file.get-custom-field-url";
import { cn } from "@/lib/style/cva.config";
import { openFile } from "@/lib/utils/download";
import { logger } from "@/logger";
import { ReadOnlyBadge } from "./read-only-badge";

export type CustomFieldFileProps = {
  label: string;
  description?: string;
  required?: boolean;
  /** #10a: render read-only (employee may view but not upload/replace/remove). */
  readOnly?: boolean;
  accept?: string;
  /** If true, convert file to base64 for JSON submission (e.g., during registration) */
  base64?: boolean;
  /** User ID - required for displaying existing files */
  userId?: string;
  /** Custom field definition ID - required for displaying existing files */
  definitionId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: FieldApi<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >;
};

export function CustomFieldFile({
  label,
  description,
  required = false,
  readOnly = false,
  accept = "image/jpeg,image/png,image/jpg,application/pdf",
  base64 = false,
  userId,
  definitionId,
  field,
}: CustomFieldFileProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const infoModalRef = useRef<HTMLIonModalElement>(null);
  /** The most recently picked file, so a slow read of an earlier pick can be ignored (#117). */
  const latestFileRef = useRef<File | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const hasError = field.state.meta.errors && field.state.meta.errors.length > 0;
  // Extract error message - handle both string and ZodError object formats
  const errorMessage = hasError
    ? typeof field.state.meta.errors[0] === "string"
      ? field.state.meta.errors[0]
      : field.state.meta.errors[0]?.message || String(field.state.meta.errors[0])
    : null;

  // Generate helper text from accept prop
  const getAcceptedFileTypesText = () => {
    const types = accept.split(",").map((type) => {
      const trimmed = type.trim();
      if (trimmed.includes("jpeg") || trimmed.includes("jpg")) {
        return "JPG";
      }
      if (trimmed.includes("png")) {
        return "PNG";
      }
      if (trimmed.includes("pdf")) {
        return "PDF";
      }
      if (trimmed.includes("image/*")) {
        return "afbeeldingen";
      }
      // Extract extension from mime type (e.g., "application/pdf" -> "PDF")
      const parts = trimmed.split("/");
      return parts[parts.length - 1].toUpperCase();
    });
    // Remove duplicates and join
    const uniqueTypes = [...new Set(types)];
    return `Toegestaan: ${uniqueTypes.join(", ")}`;
  };

  const handleFileSelect = () => {
    if (readOnly) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setFileName("");
      latestFileRef.current = null;
      field.handleChange(null);
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("fields:file.too-large", { size: "10MB" }));
      event.target.value = "";
      return;
    }

    setFileName(file.name);

    if (base64) {
      // Park the raw File first: reading it is async, and a submit inside that window must not
      // see an empty field and silently send the application without the file (#117). Callers
      // detect this state with isPendingFileValue and block until the data-URL has landed.
      latestFileRef.current = file;
      field.handleChange(file);

      // Only the most recently picked file may still touch the field value: a slow read of an
      // earlier pick must not overwrite or wipe a newer one.
      const isStillCurrent = () => latestFileRef.current === file;

      const handleReadFailure = (error?: unknown) => {
        // Stay silent about a file the user already replaced or removed: the message would point
        // at the file that is currently selected (#117).
        if (!isStillCurrent()) {
          logger.info("Ignoring failed read of a replaced file");
          return;
        }
        logger.error("Failed to read file", { error });
        toast.error(t("fields:file.read-error"));
        setFileName("");
        // Drop the parked File too, otherwise it stays queued as a file that can never be sent.
        field.handleChange(null);
      };

      // Convert file to base64 for JSON submission
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!isStillCurrent()) {
            return;
          }
          const base64String = reader.result as string;
          // Store the base64 string in the field value
          field.handleChange(base64String);
        };
        reader.onerror = () => handleReadFailure();
        reader.readAsDataURL(file);
      } catch (error) {
        handleReadFailure(error);
      }
    } else {
      // Store the File object for multipart upload
      field.handleChange(file);
    }

    event.target.value = "";
  };

  const getDisplayValue = () => {
    if (fileName) {
      return fileName;
    }
    if (field.state.value instanceof File) {
      return field.state.value.name;
    }

    // For custom field values from API (object with file and fileUrl properties)
    if (
      typeof field.state.value === "object" &&
      field.state.value !== null &&
      "file" in field.state.value
    ) {
      return field.state.value.file || null;
    }

    if (typeof field.state.value === "string" && field.state.value) {
      // Extract filename from URL or base64
      if (field.state.value.startsWith("data:")) {
        // Base64 string - try to extract original filename from stored state
        return fileName || t("fields:file.selected");
      }
      // URL - extract filename
      return field.state.value.split("/").pop() || field.state.value;
    }
    return null;
  };

  const displayValue = getDisplayValue();

  // Check if we have an existing file from the API
  const hasExistingFile =
    typeof field.state.value === "object" &&
    field.state.value !== null &&
    "file" in field.state.value &&
    field.state.value.file;

  // Fetch signed URL for existing file
  const {
    data: signedUrl,
    isLoading,
    error,
    isError,
  } = useQuery({
    ...queryOptions_custom_field_file_url(
      userId || "",
      definitionId || "",
      hasExistingFile ? (field.state.value as any).file : "",
    ),
    enabled: !!userId && !!definitionId && !!hasExistingFile,
  });

  // Debug logging for query state
  logger.info("Custom field file query state:", {
    hasExistingFile,
    userId,
    definitionId,
    filename: hasExistingFile ? (field.state.value as any).file : "",
    isLoading,
    isError,
    error,
    signedUrl,
    queryEnabled: !!userId && !!definitionId && !!hasExistingFile,
  });

  const handleDownload = () => {
    logger.info("Custom field file download clicked", {
      hasSignedUrl: !!signedUrl,
      signedUrlData: signedUrl,
      userId,
      definitionId,
      hasExistingFile,
      fieldValue: field.state.value,
    });

    if (signedUrl?.url) {
      logger.info("Opening file with URL:", signedUrl.url);
      openFile(signedUrl.url);
    } else {
      logger.error("No signed URL available to open");
      toast.error(t("common:error.download-failed"));
    }
  };

  const handleRemoveFile = () => {
    if (readOnly) {
      return;
    }
    setFileName("");
    // Invalidate any in-flight read, so it cannot repopulate the field after removal (#117).
    latestFileRef.current = null;
    field.handleChange(null);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Label */}
      <div className="flex items-center gap-1.5">
        <label className="text-md font-semibold text-txt-primary">
          {label}
          {required && <span className="text-error-120"> *</span>}
        </label>
        <ReadOnlyBadge readOnly={readOnly} />
        {description && (
          <button
            type="button"
            onClick={() => infoModalRef.current?.present()}
            className="flex h-6 w-6 items-center justify-center"
          >
            <IonIcon icon={informationCircleOutline} className="text-xl text-txt-tertiary" />
          </button>
        )}
      </div>

      {/* File upload area */}
      <div className="relative flex flex-col gap-2">
        {/* Remove button - only show when there's a file and the field is editable */}
        {!readOnly && (hasExistingFile || fileName || field.state.value) && (
          <button
            type="button"
            onClick={handleRemoveFile}
            aria-label={t("common:a11y.remove-file")}
            className="touch-target-positioned absolute right-2 top-2 z-10 flex size-8 items-center justify-center rounded-full bg-white shadow-sm transition-colors hover:bg-neutral-50"
          >
            <IonIcon icon={closeOutline} className="text-xl text-txt-primary" />
          </button>
        )}

        {/* Show "Bekijk bestand" button if file exists, otherwise show upload area */}
        {hasExistingFile && signedUrl ? (
          <button
            type="button"
            onClick={handleDownload}
            className="hover:bg-button-tertiary/80 flex h-[100px] w-full flex-col items-center justify-center gap-1 rounded-xl bg-button-tertiary transition-colors"
          >
            {/* File icon */}
            <svg
              className="text-brand-primary size-10"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                stroke="var(--color-txt-brand)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 2V9H20"
                stroke="var(--color-txt-brand)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            {/* Button text */}
            <p className="text-center text-md font-medium text-txt-brand">Bekijk bestand</p>
          </button>
        ) : (
          <button
            type="button"
            onClick={handleFileSelect}
            disabled={readOnly}
            className={cn(
              "hover:bg-button-tertiary/80 flex h-[100px] w-full flex-col items-center justify-center gap-1 rounded-xl bg-button-tertiary transition-colors",
              readOnly && "cursor-not-allowed opacity-50 hover:bg-button-tertiary",
            )}
          >
            {/* File icon */}
            <svg
              className="text-brand-primary size-10"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z"
                stroke="var(--color-txt-brand)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 2V9H20"
                stroke="var(--color-txt-brand)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            {/* Upload text or file name */}
            <p className="text-center text-md font-medium text-txt-brand">
              {displayValue || (readOnly ? t("fields:file.none") : t("fields:file.upload"))}
            </p>
          </button>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        data-testid="custom-field-file-input"
      />

      {/* Default helper text for file types */}
      {!hasError && (
        <p className="text-sm font-medium leading-[1.4] text-txt-secondary">
          {getAcceptedFileTypesText()}
        </p>
      )}

      {/* Error message */}
      {hasError && (
        <p className="text-sm font-medium leading-[1.4] text-error-120">{errorMessage}</p>
      )}

      {/* Info Modal */}
      {description && (
        <ModalSheetAutoHeight ref={infoModalRef}>
          <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
            {/* Header with title and close */}
            <div className="flex shrink-0 items-center justify-between">
              <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">{label}</h2>
              <button
                type="button"
                onClick={() => infoModalRef.current?.dismiss()}
                className="flex size-12 items-center justify-center"
              >
                <IonIcon icon={closeOutline} className="text-2xl text-txt-primary" />
              </button>
            </div>

            {/* Content - properly handles whitespace with whitespace-pre-line */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <p className="whitespace-pre-line text-md font-medium leading-[1.5] text-txt-tertiary">
                {description}
              </p>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => infoModalRef.current?.dismiss()}
              className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-md font-semibold text-txt-inverted"
            >
              Begrepen
            </button>
          </div>
        </ModalSheetAutoHeight>
      )}
    </div>
  );
}
